import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
    Button,
    DatePicker,
    FileUpload,
    Select,
    TextInput,
} from 'components/Form'
import { createApiRecord, updateApiRecord } from 'services/ClientCompanyService'
import { useDispatch, useSelector } from 'react-redux'
import { getRecord } from './store/dataSlice'
import reducer from './store'
import { injectReducer } from 'store'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { Success } from 'utils/alerts'
import dayjs from 'dayjs'
import { yesNoOptions } from 'utils/options'
injectReducer('clientCompany', reducer)

const validationSchema = Yup.object().shape({
    company: Yup.number().required('Please select company'),
    handles_own_transport: Yup.boolean().required('This filed is required'),
    name: Yup.string().required('Name is Required'),
    email: Yup.string().required('Email is required').email('Email is invalid'),
    company_ABN: Yup.string()
        .max(12, 'Company ABN Number must be at most 12 digits')
        .required('Company ABN Number Required'),
    mobile: Yup.number()
        .max(999999999999, 'Company phone number must be at most 12 digits')
        .required('Company phone number is required'),
    address: Yup.string().required('Company Address Required'),
    date_of_establishment: Yup.date().optional().nullable(),
    variance: Yup.number().optional().nullable(),
    website_url: Yup.string().optional().nullable(),
})

const Create = () => {
    const { id } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const { companyData } = useSelector((state) => state.listData)
    const { view, record, loading } = useSelector(
        (state) => state.clientCompany.data,
    )

    useEffect(() => {
        if (id) {
            fetchData(id)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const fetchData = (id) => dispatch(getRecord(id))

    const handleDiscard = () => navigate('/company-management/client-company')

    const handleSubmit = (values, setSubmitting) => {
        const data = {
            three_pl_company_id: values.company,
            handles_own_transport: values.handles_own_transport,
            client: {
                name: values.name,
                address: values.address,
                email: values.email,
                mobile: values.mobile,
                date_of_establishment: dayjs(
                    values.date_of_establishment,
                ).format('YYYY-MM-DD'),
                company_ABN: values.company_ABN,
                variance: values.variance,
                website_url: values.website_url,
                company_logo: values.company_logo,
            },
        }
        setSubmitting(true)
        if (id) {
            updateApiRecord(id, data)
                .then(() => {
                    Success('Updated Successfully!')
                    handleDiscard()
                    setSubmitting(false)
                })
                .catch((error) => {
                    setSubmitting(false)
                    return error
                })
        } else {
            createApiRecord(data)
                .then(() => {
                    Success('Created Successfully!')
                    handleDiscard()
                    setSubmitting(false)
                })
                .catch((error) => {
                    setSubmitting(false)
                    return error
                })
        }
    }

    if (loading) {
        return null
    }

    return (
        <>
            <div className="flex flex-col gap-1 mb-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Client Company
                </h2>
            </div>
            <div className="flex flex-col items-center">
                <div className="w-full bg-white border rounded-lg border-stroke shadow-default dark:border-strokedark dark:bg-boxdark">
                    <div className="px-5 py-3 border-b border-stroke dark:border-strokedark">
                        <h3 className="font-medium text-black dark:text-white">
                            {id
                                ? 'Update Client Company'
                                : 'Add New Client Company'}
                        </h3>
                    </div>
                    <div className="flex flex-col gap-3 p-6">
                        <Formik
                            initialValues={id ? record : view}
                            validationSchema={validationSchema}
                            onSubmit={(values, { setSubmitting }) => {
                                handleSubmit(values, setSubmitting)
                            }}
                        >
                            {({ values, touched, errors, isSubmitting }) => (
                                <Form>
                                    <div className="grid grid-cols-1 gap-x-4 gap-y-4 md:grid-cols-6">
                                        <Field name="company">
                                            {({ field, form }) => (
                                                <Select
                                                    options={companyData.filter(
                                                        (item) =>
                                                            item?.company_type
                                                                ?.name ===
                                                            '3PL Company',
                                                    )}
                                                    label="Parent Company"
                                                    size="sm"
                                                    placeholder="Select"
                                                    name="company"
                                                    className="md:col-span-2"
                                                    error={
                                                        errors.company &&
                                                        touched.company
                                                    }
                                                    message={errors.company}
                                                    value={values?.company}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="name">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Name"
                                                    name="name"
                                                    error={
                                                        errors.name &&
                                                        touched.name
                                                    }
                                                    message={errors.name}
                                                    value={values?.name}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="email">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Email"
                                                    name="email"
                                                    error={
                                                        errors.email &&
                                                        touched.email
                                                    }
                                                    message={errors.email}
                                                    value={values?.email}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="handles_own_transport">
                                            {({ field, form }) => (
                                                <Select
                                                    options={yesNoOptions}
                                                    label="Handles own transport"
                                                    size="sm"
                                                    placeholder="Select"
                                                    name="handles_own_transport"
                                                    className="md:col-span-2"
                                                    error={
                                                        errors.handles_own_transport &&
                                                        touched.handles_own_transport
                                                    }
                                                    message={
                                                        errors.handles_own_transport
                                                    }
                                                    value={
                                                        values?.handles_own_transport
                                                    }
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="company_ABN">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="ABN Number"
                                                    name="company_ABN"
                                                    error={
                                                        errors.company_ABN &&
                                                        touched.company_ABN
                                                    }
                                                    message={errors.company_ABN}
                                                    value={values?.company_ABN}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="mobile">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Phone Number"
                                                    name="mobile"
                                                    error={
                                                        errors.mobile &&
                                                        touched.mobile
                                                    }
                                                    message={errors.mobile}
                                                    value={values?.mobile}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="address">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Address"
                                                    name="address"
                                                    error={
                                                        errors.address &&
                                                        touched.address
                                                    }
                                                    message={errors.address}
                                                    value={values?.address}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="date_of_establishment">
                                            {({ field, form }) => (
                                                <DatePicker
                                                    className="md:col-span-2"
                                                    label="Establishment Date"
                                                    name="date_of_establishment"
                                                    error={
                                                        errors.date_of_establishment &&
                                                        touched.date_of_establishment
                                                    }
                                                    message={
                                                        errors.date_of_establishment
                                                    }
                                                    value={
                                                        values?.date_of_establishment
                                                            ? dayjs(
                                                                  values?.date_of_establishment,
                                                              ).format(
                                                                  'YYYY-MM-DD',
                                                              )
                                                            : ''
                                                    }
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="variance">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Total Allowed Variance"
                                                    name="variance"
                                                    error={
                                                        errors.variance &&
                                                        touched.variance
                                                    }
                                                    message={errors.variance}
                                                    value={values?.variance}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="website_url">
                                            {({ field, form }) => (
                                                <TextInput
                                                    type="text"
                                                    className="md:col-span-2"
                                                    label="Website"
                                                    name="website_url"
                                                    error={
                                                        errors.website_url &&
                                                        touched.website_url
                                                    }
                                                    message={errors.website_url}
                                                    value={values?.website_url}
                                                    onChange={(e) =>
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.value,
                                                        )
                                                    }
                                                />
                                            )}
                                        </Field>
                                        <Field name="company_logo">
                                            {({ field, form }) => (
                                                <FileUpload
                                                    label="Logo"
                                                    className="md:col-span-2"
                                                    name="company_logo"
                                                    onChange={(e) => {
                                                        form.setFieldValue(
                                                            field.name,
                                                            e.target.files[0],
                                                        )
                                                    }}
                                                />
                                            )}
                                        </Field>
                                    </div>
                                    <div className="flex items-center justify-end gap-4 mt-3">
                                        <Button
                                            className="h-10 w-18 text-primary border border-primary dark:text-secondary dark:border-secondary"
                                            onClick={handleDiscard}
                                        >
                                            Discard
                                        </Button>
                                        <Button
                                            type="submit"
                                            className="flex items-center justify-center px-6 py-2 text-white transition border rounded-lg cursor-pointer hover:bg-opacity-90 border-primary bg-primary dark:border-secondary dark:bg-secondary"
                                            disabled={isSubmitting}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Create
